import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, LineChart, Line, Tooltip } from 'recharts';
import { useLocale } from '@/hooks/useLocale';
import { useToast } from '@/components/Toast';
import { MonitorOverviewRequest, MonitorOverviewData, TimePeriod } from "@/types/datachart"
import { claimableReward } from '@/services/datachart';
import { useOverviewDataSections } from '@/hooks/useOverviewData';
import { useNotificationWebSocket } from '@/hooks/useNotificationWebSocket'; // Add this import
import UserStore from '@/store/UserStore';
import bellIcon from '@/assets/dataChart/bell-icon.svg';
import upLineIcon from '@/assets/dataChart/up-line-icon.svg'
import downLineIcon from '@/assets/dataChart/down-line-icon.svg'
import avatarIcon from '@/assets/dataChart/avatar-icon.svg'
import icon from '@/assets/svg/首页.svg'

export const routeConfig: RouteConfig = {
	title: 'route.overview',
	icon: <img src={icon} alt="Data Chart" className="w-4 h-4" />,
	layout: true,
	sort: 0
};

const Overview = () => {
  const { t } = useLocale();
  const toast = useToast();

  // Use WebSocket hook for real-time notifications (replaces getNotificationData)
  const { notifications, isConnected: wsConnected } = useNotificationWebSocket();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('all'); // Store selected time period
  const [userId, setUserId] = useState<string | null>(null);
  const [userRewardRatio, setUserRewardRatio] = useState<number>(0);
  const [userSpotRatio, setUserSpotRatio] = useState<number>(0);
  const [periodDropdownOpen, setPeriodDropdownOpen] = useState(false);

  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  // Default mock chart data / 默认模拟图表数据
  const defaultCommissionChartData = [
    { name: '1', value: 120 },
    { name: '2', value: 80 },
    { name: '3', value: 150 },
    { name: '4', value: 90 },
    { name: '5', value: 110 },
    { name: '6', value: 70 },
    { name: '7', value: 95 },
    { name: '8', value: 130 },
    { name: '9', value: 85 },
    { name: '10', value: 140 },
    { name: '11', value: 75 },
    { name: '12', value: 105 }
  ];

  const defaultCustomersChartData = [
    { name: 'Jan', value: 45 },
    { name: 'Feb', value: 67 },
    { name: 'Mar', value: 89 },
    { name: 'Apr', value: 34 },
    { name: 'May', value: 78 },
    { name: 'Jun', value: 56 },
    { name: 'Jul', value: 92 },
    { name: 'Aug', value: 43 },
    { name: 'Sep', value: 71 },
    { name: 'Oct', value: 63 },
    { name: 'Nov', value: 84 },
    { name: 'Dec', value: 58 }
  ];

  // Add period options
  const periodOptions = [
    { value: 'all' as TimePeriod, label: 'All' },
    { value: 'today' as TimePeriod, label: 'Today' },
    { value: 'yesterday' as TimePeriod, label: 'Yesterday' },
    { value: '7days' as TimePeriod, label: 'Last 7 Days' },
    { value: '30days' as TimePeriod, label: 'Last 30 Days' },
    { value: '1year' as TimePeriod, label: 'Last Year' },
  ];

  // Extract data from API using custom hook / 使用自定义钩子从API提取数据
  const {
    overall,           // Total rewards, invites, users, DAU / 总奖励、邀请、用户、DAU
    period,           // Current period metrics / 当前周期指标
    comparison,       // Previous period data / 上一周期数据
    growth,          // Growth rates / 增长率
    lastUpdated,     // Timestamp / 时间戳
    inviteCodes,     // Invite codes array / 邀请码数组
    recentDirectUsers, // Recent users array / 最近用户数组
    commissionChartData = defaultCommissionChartData, // Commission chart data with fallback / 佣金图表数据（带默认值）
    customersChartData = defaultCustomersChartData, // Customer chart data with fallback / 客户图表数据（带默认值）
    loading,
    error,
    refetch
  } = useOverviewDataSections({
    period: selectedPeriod,
    page_no: 1,
    page_size: 10
  });

  const activeUsersPercentage = overall && overall.total_invite
    ? (overall.dau / overall.total_invite) * 100
    : 0;

  // Update handleDropdownToggle function to handle period dropdown
  const handleDropdownToggle = (fieldName: string) => {
    if (fieldName === 'period') {
      setPeriodDropdownOpen(prev => !prev);
    }
  };

  const formatPercentage = (num: number | string | null | undefined) => {
    const n = Number(num);
    if (isNaN(n) || num === '0') return '0';
    const str = n.toString();
    const decimal = str.split('.')[1];
    if (decimal && decimal.length > 2) {
      return n.toFixed(2);
    }
    return str;
  };

  const formatNumber = (num: number | string | null | undefined) => {
    if (num === null || num === undefined) return '0.00';
    if (isNaN(Number(num))) return '0.00';
    // 1000000.023024 = 1,000,000.02
    return Number(num).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  useEffect(() => {
    // Fetch user info when component mounts
    const fetchUserInfo = async () => {
      const userId = await UserStore.userId;
      const rewardRatio = await UserStore.userRewardRatio;
      const spotRatio = await UserStore.userSpotRatio;
      setUserId(userId);
      setUserRewardRatio(rewardRatio);
      setUserSpotRatio(spotRatio);
    };

    fetchUserInfo();
  }, []);

  // Auto-scroll notifications every 3 seconds - now using WebSocket notifications
  useEffect(() => {
    if (notifications.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % notifications.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [notifications.length]);

  const handleChange = (value: TimePeriod) => {
    console.log(`selected ${value}`);
    setSelectedPeriod(value);
    
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Debounce API call by 300ms
    debounceTimeoutRef.current = setTimeout(() => {
      refetch({ period: value });
    }, 300);
  };

  const handleClaim = async () => {
    // Handle claim action
    try {
      console.log('Claim clicked', overall?.claimable);
      await claimableReward();
      await refetch();
    } catch (error) {
      toast.error({message: t('overview.claimError')});
    }
  };

  const handleCopyCode = (inviteCode: string) => {
    navigator.clipboard.writeText(`${inviteCode}`);
    toast.success({message: t('overview.codeCopied')});
  };

  const handleShareLink = (inviteCode: string) => {
    navigator.clipboard.writeText(`https://app.qubit.trade/join/${inviteCode}`);
    toast.success({message: t('overview.linkCopied')});
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
      {/* Loading state / 加载状态 */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="text-sel-normal dark:text-dark-sel-normal text-sm md:text-base">{t('overview.loadingOverviewData')}</div>
        </div>
      )}
      
      {/* Error state / 错误状态 */}
      {error && (
        <div className="bg-red-600 text-sel-normal dark:text-dark-sel-normal p-4 rounded-lg mb-5">
          <div className="font-semibold text-sm md:text-base">{t('overview.errorLoadingData')}</div>
          <div className="text-sm md:text-base">{error}</div>
        </div>
      )}

      {/* 顶部滚动通知栏 / Top scrolling notification bar - Updated to use WebSocket */}
      <div className="mb-5">
        <div className="bg-card-background dark:bg-dark-card-background rounded-lg border border-normal dark:border-dark-normal px-6 py-3 overflow-hidden">
          <div className="flex items-center text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-normal">
            <span className="flex-shrink-0 mr-4 flex items-center">
              <img src={bellIcon} alt="" className="w-4 h-4" />
              {/* WebSocket connection status indicator */}
              <div 
                className={`ml-2 w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-gray-400'}`}
                title={wsConnected ? 'Real-time connected' : 'Using cached data'}
              />
            </span>
            <div className="flex-1 overflow-hidden relative">
              <div className="animate-scroll-left flex whitespace-nowrap">
                {/* Use WebSocket notifications instead of mock data */}
                {[...notifications, ...notifications].map((notification, index) => (
                  <span
                    key={index}
                    className="inline-block mr-12 flex-shrink-0"
                  >
                    {notification}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 中部内容 / Middle content */}
      <div className='flex flex-col xl:flex-row justify-between w-full items-stretch gap-6'>
        {/* 中部左侧内容 / Left side content */}
        <div className='flex-1 flex flex-col mb-3 xl:mb-0'>
          {/* 左侧顶部数据概括以及搜索位置 / Top data summary and search area */}
          <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 md:mb-5 gap-3'>
            <div className='flex flex-col sm:flex-row items-start sm:items-center'>
              <span className='min-w-[140px] text-sel-normal dark:text-dark-sel-normal font-normal text-xl font-inter leading-normal mr-0 sm:mr-2.5 mb-1 sm:mb-0'>{t('overview.overview')}</span>
              <span className='text-normal dark:text-dark-normal font-normal text-xs font-inter leading-normal overflow-hidden text-ellipsis line-clamp-2'>
                {t('overview.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
              </span>
            </div>
            <div className='flex flex-col sm:flex-row items-stretch sm:items-center w-full h-full sm:w-auto gap-2'>
              <div className={`relative z-10 w-full sm:w-[120px]`} onClick={e => e.stopPropagation()}>
                  <div
                      className={
                          `flex items-center cursor-pointer h-10 px-3 bg-card-background dark:bg-dark-card-background border border-normal dark:border-dark-normal rounded-md`
                      }
                      onClick={() => handleDropdownToggle('period')}
                  >
                      <div className="flex-1 text-xs font-inter font-normal text-sel-normal dark:text-dark-sel-normal truncate">
                            {
                              periodOptions.find(
                                  option => option.value === selectedPeriod
                              )?.label || t('invitationPromotion.invitationLink.settings.notSelected')
                          }
                      </div>
                      <div className="ml-1 flex items-center">
                          <svg className={`h-3 w-3 transition-transform duration-300 ${periodDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="#A1A3C4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                  </div>
                  <div className={`absolute left-0 mt-1 p-[10px] bg-button dark:bg-dark-button rounded-md shadow-lg z-30 min-w-full transition-all border border-normal duration-300 origin-top inline-flex flex-col justify-start items-start ${periodDropdownOpen ? 'opacity-100 scale-y-100 transform' : 'opacity-0 scale-y-0 transform pointer-events-none'}`}> 
                      {(periodOptions || []).map(option => (
                          <div
                              key={option.value}
                              className={`self-stretch h-6 py-1 inline-flex justify-start items-center cursor-pointer hover:bg-button-hover dark:hover:bg-dark-button-hover ${option.value === selectedPeriod ? 'text-sel-normal dark:text-dark-sel-normal' : 'text-normal dark:text-dark-normal'}`}
                              onClick={e => {
                                  e.stopPropagation();
                                  handleChange(option.value);
                                  setPeriodDropdownOpen(false);
                          }}
                          >
                              <div className="justify-start text-xs font-inter font-normal leading-tight truncate">
                                  {option.label}
                              </div>
                          </div>
                      ))}
                  </div>
              </div>
              <span>
                <button 
                  className="bg-button dark:bg-dark-button text-sel-normal dark:text-dark-sel-normal font-normal text-sm font-inter leading-tight rounded-lg h-10 w-full sm:min-w-[100px] px-5 py-2.5 truncate whitespace-nowrap overflow-hidden"
                >
                  {t('overview.overview')}
                </button>
              </span>
              <span>
                <button 
                  className="bg-transparent opacity-60 border border-dark-main dark:border-main text-sel-normal dark:text-dark-sel-normal font-normal text-sm font-inter leading-tight rounded-lg h-10 w-full sm:min-w-[100px] px-5 py-2.5 truncate whitespace-nowrap overflow-hidden"
                  onClick={() => handleShareLink(inviteCodes?.[0]?.invite_code || '')}
                  disabled={!inviteCodes || inviteCodes.length === 0}
                >
                  {t('overview.shareLink')}
                </button>
              </span>
            </div>
          </div>
          {/* 左侧卡片区域 - 使用flex-1让它自动填充剩余空间 / Left card area - using flex-1 for auto-fill remaining space */}
          <div className='flex flex-col md:flex-row w-full flex-1 gap-6'>
            {/* 左边小卡片列 / Left small cards column */}
            <div className='flex-1 flex flex-col gap-6'>
              {/* 左上角小卡片 - flex-1让它占用一半空间 / Top-left small card - flex-1 for half space */}
              <div className='bg-card-background dark:bg-dark-card-background rounded-lg p-6 flex-1 flex flex-col justify-between min-h-[335px]'>
                {/* 头部 / Header */}
                <div className='flex justify-between items-start flex-1'>
                  {/* 头部左侧 / Commission left side */}
                  <div className='flex flex-col h-full'>
                    <div>
                      <div className='flex justify-start items-center'>
                        <span className="text-normal dark:text-dark-normal font-normal text-sm font-inter leading-normal mr-1">{t('overview.totalCommission')}</span>
                      </div>
                      <div className='mt-5 text-sel-normal dark:text-dark-sel-normal text-2xl font-normal font-inter leading-normal'>{(period?.period_reward === null || period?.period_reward === undefined || period?.period_reward === '') ? '--' : period?.period_reward}</div>
                    </div>
                    {/* <div className='text-normal dark:text-dark-normal font-normal text-xs font-inter leading-normal items-center'>
                      <span className='items-center border-b border-dashed border-underline dark:border-dark-underline'>{t('overview.selfRebate')}</span>
                      <span className='ml-2 text-normal dark:text-dark-normal text-base font-normal font-inter leading-normal'>{formatNumber(overall?.direct_reward) || '0'}</span>
                    </div> */}
                  </div>
                  {/* 头部右侧 / Commission right side */}
                  <div className='flex flex-col items-center justify-center space-y-2'>
                    <div>
                      <img src={growth?.commission_growth_rate?.startsWith('-') ? downLineIcon : upLineIcon} alt="" className="w-4 h-4" />
                    </div>
                    <div className={`text-base font-normal font-inter leading-normal ${growth?.commission_growth_rate?.startsWith('-') ? 'text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text' : 'text-place-order-bt-text dark:text-dark-place-order-bt-text'}`}>
                      {growth?.commission_growth_rate?.startsWith('-') ? '' : '+ '}{ formatPercentage(growth?.commission_growth_rate) || '0'}%
                    </div>
                    <div className='text-normal dark:text-dark-normal text-sm font-normal font-inter leading-normal text-center'>{t('overview.comparison')}</div>
                  </div>
                </div>
                {/* Commission Chart Data - Line chart for commission data */}
                <div className='w-full h-full'>
                  <div style={{ width: '100%', height: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        width={266}
                        height={145}
                        data={commissionChartData}
                        margin={{ top: 5, right: 3, left: 0, bottom: 5 }}
                      >
                        <defs>
                          <linearGradient id="commissionLineGradient" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#2D42FC" />
                            <stop offset="100%" stopColor="#A1AAFF" />
                          </linearGradient>
                        </defs>
                        <XAxis 
                          dataKey="name" 
                          axisLine={false}
                          tickLine={false}
                          tick={false}
                          height={1}
                        />
                        <YAxis
                          dataKey="value"
                          axisLine={false}
                          tickLine={false}
                          tick={false}
                          width={5}
                        />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#1E203E',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#ffffff',
                            fontSize: '12px',
                            fontFamily: 'Inter',
                            padding: '8px 12px',
                            width: 'auto'
                          }}
                          labelStyle={{ 
                            color: '#ffffff',
                            fontSize: '12px',
                            fontWeight: 'normal',
                            marginBottom: '4px'
                          }}
                          formatter={(value, name) => [
                            <div className="flex gap-2 justify-between text-sel-normal dark:text-dark-sel-normal font-normal text-xs font-inter leading-normal" key={name}>
                              <div>{t('overview.totalCommissionTooltip')}</div> <div>{value}</div>
                            </div>
                          ]}
                          labelFormatter={(label) => (
                            <div className="flex gap-2 justify-between text-sel-normal dark:text-dark-sel-normal font-normal text-xs font-inter leading-normal">
                              <div>{t('overview.time')}</div> <div>{label}</div>
                            </div>
                          )}
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="url(#commissionLineGradient)"
                          strokeWidth={2}
                          dot={{ fill: '#2D42FC', r: 2 }}
                          activeDot={{ r: 4, fill: '#2D42FC' }}
                          isAnimationActive={loading}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className='mt-1 text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none'>{t('overview.totalVolumeNote')}</div>
              </div>

              {/* 左下角小卡片 - flex-1让它占用一半空间 / Bottom-left small card - flex-1 for half space */}
              <div className='bg-card-background dark:bg-dark-card-background rounded-lg p-6 flex justify-between flex-2 items-center min-h-[140px]'>
                <div className='flex flex-col justify-center space-y-5'>
                  <div className='flex items-center'>
                    <span className='text-normal dark:text-dark-normal mr-1 text-sm font-normal font-inter leading-normal'>{t('overview.netFee')}</span>
                  </div>
                  <div className='text-sel-normal dark:text-dark-sel-normal text-2xl font-normal font-inter leading-normal'>{(period?.period_net_trade_fee_usdt === null || period?.period_net_trade_fee_usdt === undefined || period?.period_net_trade_fee_usdt === '') ? '--' : period?.period_net_trade_fee_usdt}</div>
                  <div className='mt-1 text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none'>{t('overview.totalVolumeNote')}</div>
                </div>
                <div className='flex flex-col items-center justify-center space-y-2'>
                  <div>
                    <img src={growth?.net_trade_fee_growth_rate?.startsWith('-') ? downLineIcon : upLineIcon} alt="" className="w-4 h-4" />
                  </div>
                  <div className={`text-base font-normal font-inter leading-normal ${growth?.net_trade_fee_growth_rate?.startsWith('-') ? 'text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text' : 'text-place-order-bt-text dark:text-dark-place-order-bt-text'}`}>
                    {growth?.net_trade_fee_growth_rate?.startsWith('-') ? '' : '+ '}{formatPercentage(growth?.net_trade_fee_growth_rate) || '0'}%
                  </div>
                  <div className='text-normal dark:text-dark-normal text-sm font-normal font-inter leading-normal text-center'>{t('overview.comparison')}</div>
                </div>
              </div>
            </div>

            {/* 右边小卡片列 / Right small cards column */}
            <div className='flex-1 flex flex-col gap-6'>
              {/* 右侧上面小卡片 - flex-1让它占用一半空间 / Top-right small card - flex-1 for half space */}
              <div className='bg-card-background dark:bg-dark-card-background rounded-lg p-6 flex justify-between flex-2 items-center min-h-[140px]'>
                <div className='flex flex-col justify-center space-y-5'>
                  <div className='flex items-center'>
                    <span className='text-normal dark:text-dark-normal mr-1 text-sm font-normal font-inter leading-normal'>{t('overview.totalVolume')}</span>
                  </div>
                  <div className='text-sel-normal dark:text-dark-sel-normal text-2xl font-normal font-inter leading-normal'>{formatNumber(period?.period_volume_usdt) || '0'}</div>
                  <div className='mt-1 text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none'>{t('overview.totalVolumeNote')}</div>
                </div>
                <div className='flex flex-col items-center justify-center space-y-2'>
                  <div>
                    <img src={growth?.volume_growth_rate?.startsWith('-') ? downLineIcon : upLineIcon} alt="" className="w-4 h-4" />
                  </div>
                  <div className={`text-base font-normal font-inter leading-normal ${growth?.volume_growth_rate?.startsWith('-') ? 'text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text' : 'text-place-order-bt-text dark:text-dark-place-order-bt-text'}`}>
                    {growth?.volume_growth_rate?.startsWith('-') ? '' : '+ '}{formatPercentage(growth?.volume_growth_rate) || '0'}%
                  </div>
                  <div className='text-normal dark:text-dark-normal text-sm font-normal font-inter leading-normal text-center'>{t('overview.comparison')}</div>
                </div>
              </div>

              {/* 右侧下面小卡片 - flex-1让它占用一半空间 / Bottom-right small card - flex-1 for half space */}
              <div className='bg-card-background dark:bg-dark-card-background rounded-lg p-6 flex-1 flex flex-col min-h-[335px]'>
                <div className='flex justify-between'>
                  <div className='space-y-5'>
                    <div className='flex items-center'>
                      <span className='text-normal dark:text-dark-normal mr-1 text-sm font-normal font-inter leading-normal'>{t('overview.customerCount')}</span>
                    </div>
                    <div className='text-sel-normal dark:text-dark-sel-normal text-2xl font-normal font-inter leading-normal'>{period?.period_customers || '0'}</div>
                  </div>
                  <div className='flex flex-col items-center space-y-2'>
                    <div>
                      <img src={growth?.customer_growth_rate?.startsWith('-') ? downLineIcon : upLineIcon} alt="" className="w-4 h-4" />
                    </div>
                    <div className={`text-base font-normal font-inter leading-normal ${growth?.customer_growth_rate?.startsWith('-') ? 'text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text' : 'text-place-order-bt-text dark:text-dark-place-order-bt-text'}`}>
                      {growth?.customer_growth_rate?.startsWith('-') ? '' : '+ '}{formatPercentage(growth?.customer_growth_rate) || '0'}%
                    </div>
                    <div className='text-normal dark:text-dark-normal text-sm font-normal font-inter leading-normal text-center'>{t('overview.comparison')}</div>
                  </div>
                </div>

                {/* 图表 - 使用flex-1让它填充剩余空间 / Chart - using flex-1 to fill remaining space */}
                <div className="flex-1 min-h-32">
                  <div style={{ width: '100%', height: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        width={300}
                        height={128}
                        data={customersChartData}
                        margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
                        barCategoryGap="20%"
                      >
                        <defs>
                          <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#7785FF" stopOpacity={1} />
                            <stop offset="100%" stopColor="#F3F3F3" stopOpacity={1} />
                          </linearGradient>
                        </defs>

                        <XAxis
                          dataKey="name"
                          axisLine={false}
                          tickLine={false}
                          tick={false}
                          height={1}
                        />
                        <YAxis 
                          axisLine={false}
                          tickLine={false}
                          tick={false}
                          width={1}
                        />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: '#1E203E',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#ffffff',
                            fontSize: '12px',
                            fontFamily: 'Inter',
                            padding: '8px 12px',
                            width: 'auto'
                          }}
                          labelStyle={{ 
                            color: '#ffffff',
                            fontSize: '12px',
                            fontWeight: 'normal',
                            marginBottom: '4px'
                          }}

                          formatter={(value, name) => [
                            <div className="flex gap-2 justify-between text-sel-normal dark:text-dark-sel-normal font-normal text-xs font-inter leading-normal" key={name}>
                              <div>{t('overview.customer')}</div> <div>{value}</div>
                            </div>
                          ]}
                          labelFormatter={(label) => (
                            <div className="flex gap-2 justify-between text-sel-normal dark:text-dark-sel-normal font-normal text-xs font-inter leading-normal">
                              <div>{t('overview.month')}</div> <div>{label}</div>
                            </div>
                          )}
                          cursor={false}
                        />

                        <Bar
                          dataKey="value"
                          fill="url(#barGradient)"
                          radius={[5, 5, 0, 0]}
                          maxBarSize={16}
                          isAnimationActive={loading}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className='mt-2 text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none'>{t('overview.customerCountNote')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* 中部右侧内容 / Right side content */}
        <div className='w-full flex flex-col xl:min-w-[350px] xl:max-w-[400px] p-6 bg-card-background dark:bg-dark-card-background rounded-lg'>
          {/* 顶部数据 / Top data */}
          <div className='flex flex-col sm:flex-row h-full items-start sm:items-center mb-4'>
            {/* 头像 / Avatar */}
            <div className='mr-3'>
              <img src={avatarIcon} alt="" className="w-16 h-16" />
            </div>
            <div className='flex-1'>
              <div className='text-sel-normal dark:text-dark-sel-normal font-normal text-xl font-inter leading-normal mb-1'>
                {t('overview.hello').replace('{name}', `QuBitUser-${userId}`)}
              </div>
              {/* flex justify-between */}
              <div className='grid grid-cols-3 gap-2'> 
                <div>
                  <div className='text-normal dark:text-dark-normal text-xs font-normal font-inter leading-normal mb-1'>{t('overview.spot')}</div>
                  <div className='text-sel-normal dark:text-dark-sel-normal text-base font-normal font-inter leading-normal'>{formatPercentage(userSpotRatio)}%</div>
                </div>
                <div>
                  <div className='text-normal dark:text-dark-normal text-xs font-normal font-inter leading-normal mb-1'>{t('overview.futures')}</div>
                  <div className='text-sel-normal dark:text-dark-sel-normal text-base font-normal font-inter leading-normal'>{formatPercentage(userRewardRatio)}%</div>
                </div>
                {/* <div>
                  <div className='text-normal dark:text-dark-normal text-xs font-normal font-inter leading-normal mb-1'>{t('overview.alpha')}</div>
                  <div className='text-sel-normal dark:text-dark-sel-normal text-base font-normal font-inter leading-normal'>50%</div>
                </div> */}
              </div>
            </div>
          </div>

          {/* 提现USDT / Withdraw USDT */}
          <div className='flex flex-col sm:flex-row justify-between bg-button dark:bg-dark-button px-4 py-5 rounded-lg items-center mb-3 gap-3 sm:gap-0'>
            <div>
              <div className='text-normal dark:text-dark-normal font-normal text-sm font-inter leading-normal mb-3'>{t('overview.pendingWithdraw')}</div>
              <div className='text-sel-normal dark:text-dark-sel-normal font-semibold text-xs font-inter leading-none'>
                <span className='font-semibold mr-1'>{formatNumber(overall?.claimable)}</span>
                <span className='font-normal'>USDT</span>
              </div>
            </div>
            <button
              className='bg-tabline dark:bg-dark-tabline disabled:opacity-50 px-5 py-2.5 rounded-lg text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-tight w-full sm:w-auto'
              onClick={handleClaim}
              disabled={!overall?.claimable || Number(overall?.claimable) === 0}
            >
              <span className="w-full">{t('overview.claim')}</span>
            </button>
          </div>

          {/* 客户活跃度标题 / Customer activity title */}
          <div className='p-6 bg-button dark:bg-dark-button rounded-lg'>
            <div className='flex items-center'>
              <span className='text-sel-normal dark:text-dark-sel-normal font-normal text-lg font-inter leading-none mr-1'>{t('overview.customerActivity')}</span>
            </div>

            {/* 客户活跃度图表 - 使用flex-1让它填充剩余空间 / Customer activity chart - using flex-1 to fill remaining space */}
            <div className="flex-1 relative flex items-center justify-center min-h-48">
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <PieChart>
                  <defs>
                    <linearGradient id="gaugeGradient" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="#A1AAFF" />
                      <stop offset="100%" stopColor="#4E5EFA" />
                    </linearGradient>
                  </defs>

                  {/* 背景细线 / Background thin line */}
                  <Pie
                    data={[{ value: 100 }]}
                    cx="50%"
                    cy="75%"
                    startAngle={180}
                    endAngle={0}
                    innerRadius="80%"
                    outerRadius="100%"
                    dataKey="value"
                    fill="#2A2A3A"
                    stroke="none"
                    isAnimationActive={loading}
                  />

                  {/* 彩色粗线部分 / Colored thick line part */}
                  <Pie
                    data={[{ value: activeUsersPercentage }]}
                    cx="50%"
                    cy="75%"
                    startAngle={180}
                    endAngle={180 - (activeUsersPercentage / 100 * 180)}
                    innerRadius="83%"
                    outerRadius="97%"
                    dataKey="value"
                    fill="url(#gaugeGradient)"
                    stroke="none"
                    isAnimationActive={loading}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* 中心文字 / Center text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ top: '30%' }}>
                <div className="text-sel-normal dark:text-dark-sel-normal text-lg font-normal font-inter leading-none">
                  {formatPercentage(activeUsersPercentage)}%
                </div>
              </div>
            </div>
            {/* 活跃人数 / Active user count */}
            <div className='flex justify-between mb-3'>
              <div className='flex items-center'>
                <div className='w-3 h-3 mr-2 bg-[linear-gradient(270deg,#A1AAFF_0%,#4E5EFA_100%)]'></div>
                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">{t('overview.activeUsers')}</div>
              </div>
              <div className='text-normal dark:text-dark-normal font-normal text-xs font-inter leading-none'>{overall?.dau || '0'}</div>
            </div>
            {/* 非活跃人数 / Inactive user count */}
            <div className='flex justify-between'>
              <div className='flex items-center'>
                <div className='w-3 h-3 border-2 border-[#A1A3C4] mr-2'></div>
                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">{t('overview.inactiveUsers')}</div>
              </div>
              <div className='text-normal dark:text-dark-normal font-normal text-xs font-inter leading-none'>{(overall?.total_invite || 0) - (overall?.dau || 0)}</div>
            </div>
            <div className='mt-2 text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none'>{t('overview.customerActivityNote')}</div>
          </div>
        </div>
      </div>
      
      {/* 底部表格 / Bottom tables */}
      <div className='flex flex-col xl:flex-row mt-5 gap-6'>
        {/* 我的推广链接 / My referral links */}
        <div className='flex-1 bg-card-background dark:bg-dark-card-background rounded-lg overflow-hidden'>
          {/* 标题栏 / Title bar */}
          <div className='flex items-center justify-between p-6'>
            <div className='flex items-center justify-between'>
              <span className='text-sel-normal dark:text-dark-sel-normal font-semibold text-xl font-inter leading-normal mr-1'>{t('overview.myReferralLinks')}</span>
              <svg className="w-4 h-4 text-normal dark:text-dark-normal cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
          
          {/* 表格容器，支持横向滚动 / Table container with horizontal scroll */}
          <div className='w-full'>
            {/* 表头 / Table header */}
            <div className='grid grid-cols-5 gap-4 px-6 pt-4 pb-1 border-b border-normal text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none'>
              <div>
                <span className="border-b border-dashed border-underline dark:border-dark-underline">{t('overview.inviteCode')}</span>
              </div>
              <div>
                <span className="border-b border-dashed border-underline dark:border-dark-underline">{t('overview.inviteeCount')}</span>
              </div>
              {/* <div>
                <span className="border-b border-dashed border-underline dark:border-dark-underline">{t('overview.spotRebateRate')}</span>
              </div>
              <div>
                <span className="border-b border-dashed border-underline dark:border-dark-underline">{t('overview.futuresRebateRate')}</span>
              </div> */}
              {/* <div> <span className="border-b border-dashed border-underline dark:border-dark-underline">{t('overview.alphaRebateRate')}</span> </div> */}
              <div>
                <span className="border-b border-dashed border-underline dark:border-dark-underline">{t('overview.operation')}</span>
              </div>
            </div>
            
            {/* 表格内容 / Table content */}
            <div className='px-6 py-4'>
              {inviteCodes?.map((code, index) => (
                <div key={code.invite_code || index} className='grid grid-cols-5 gap-4 items-center text-sm mb-4'>
                  <div className='flex flex-col items-start gap-2'>
                    <div className="flex items-center">
                      <span className='text-sel-normal dark:text-dark-sel-normal font-normal text-sm font-inter leading-none mr-2 truncate'>{code.invite_code}</span>
                      <svg 
                        className="w-4 h-4 text-normal dark:text-dark-normal cursor-pointer flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        onClick={() => handleCopyCode(code.invite_code)}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {index === 0 && (
                      <div className='bg-button-hover dark:bg-dark-button-hover rounded px-2 h-5 flex items-center text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none'>{t('overview.defaultLink')}</div>
                    )}
                  </div>
                  <div className='text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none'>{code.registration_count}</div>
                  {/* <div className='flex flex-col items-start'>
                    <div className="flex flex-col sm:flex-row sm:items-center text-xs">
                      <span className='text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none mr-1'>{t('overview.myCommission')}</span>
                      <span className='text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none'>{formatPercentage(code.self_spot_rebate_rate)}%</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center text-xs mt-1">
                      <span className='text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none mr-1'>{t('overview.customerCommission')}</span>
                      <span className='text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none'>{formatPercentage(code.spot_rebate_rate)}%</span>
                    </div>
                  </div>
                  <div className='flex flex-col items-start'>
                    <div className="flex flex-col sm:flex-row sm:items-center text-xs">
                      <span className='text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none mr-1'>{t('overview.myCommission')}</span>
                      <span className='text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none'>{formatPercentage(code.self_contract_rebate_rate)}%</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center text-xs mt-1">
                      <span className='text-normal dark:text-dark-normal text-sm font-normal font-inter leading-none mr-1'>{t('overview.customerCommission')}</span>
                      <span className='text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none'>{formatPercentage(code.contract_rebate_rate)}%</span>
                    </div>
                  </div> */}
                  <div>
                    <button 
                      className='text-special-text dark:text-dark-special-text text-sm font-normal font-inter leading-normal whitespace-nowrap'
                      onClick={() => handleShareLink(code.invite_code)}
                    >
                      {t('overview.shareLink')}
                    </button>
                  </div>
                </div>
              ))}
              {(!inviteCodes || inviteCodes.length === 0) && (
                <div className='text-center text-normal dark:text-dark-normal text-sm py-4'>
                  {t('overview.noInviteCode')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 最近注册 / Recent registrations */}
        <div className='flex-1 bg-card-background dark:bg-dark-card-background rounded-lg overflow-hidden'>
          {/* 标题栏 / Title bar */}
          <div className='flex items-center justify-between p-6'>
            <div className='flex items-center'>
              <span className='text-sel-normal dark:text-dark-sel-normal font-semibold text-xl font-inter leading-normal mr-1'>{t('overview.recentRegistrations')}</span>
              <svg className="w-4 h-4 text-normal dark:text-dark-normal cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
          
          {/* 表格容器，支持横向滚动 / Table container with horizontal scroll */}
          <div className='overflow-x-auto'>
            {/* 表头 / Table header */}
            <div className='min-w-[500px] grid grid-cols-4 gap-4 px-6 pb-1 pt-4 border-b border-normal text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none'>
              <div>{t('overview.userUID')}</div>
              <div>{t('overview.registrationTime')}</div>
              <div>{t('overview.isDeposited')}</div>
              <div>{t('overview.isActive')}</div>
            </div>
            
            {/* 表格内容 / Table content */}
            <div className='min-w-[500px] px-6'>
              {recentDirectUsers?.map((user, index) => (
                <div key={user.user_id || index} className='grid grid-cols-4 gap-4 items-center py-3 text-sm border-b border-gray-800 last:border-b-0'>
                  <div className='text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none truncate'>{user.user_id}</div>
                  <div className='text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none'>{new Date(user.joined_date * 1000).toLocaleDateString()}</div>
                  <div className={user.has_deposited ? 'text-place-order-bt-text dark:text-dark-place-order-bt-text' : 'text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none'}>
                    {user.has_deposited ? t('overview.yes') : t('overview.no')}
                  </div>
                  <div className={user.has_traded ? 'text-place-order-bt-text dark:text-dark-place-order-bt-text' : 'text-place-order-bt-sell-text dark:text-dark-place-order-bt-sell-text text-sm font-normal font-inter leading-none'}>
                    {user.has_traded ? t('overview.yes') : t('overview.no')}
                  </div>
                </div>
              ))}
              {(!recentDirectUsers || recentDirectUsers.length === 0) && 
                <div className='text-center text-normal dark:text-dark-normal text-sm py-4'>
                  {t('overview.noRecentRegistrations')}
                </div>
              }
            </div>
          </div>
          <div className='mt-2 px-6 pb-4 text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none'>
            {t('overview.recentRegistrationsNote')}
          </div>
        </div>
      </div>
      

      {/* 自定义 CSS 动画 / Custom CSS animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes scroll-left {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-50%); }
          }
          .animate-scroll-left {
            animation: scroll-left 30s linear infinite;
          }
          .animate-scroll-left:hover {
            animation-play-state: paused;
          }
          
          .animate-scroll-left-2 {
            animation: scroll-left 25s linear infinite;
          }
          .animate-scroll-left-2:hover {
            animation-play-state: paused;
          }
          
          .animate-scroll-left-3 {
            animation: scroll-left 20s linear infinite;
          }
          .animate-scroll-left-3:hover {
            animation-play-state: paused;
          }

          /* 移除图表点击边框 / Remove chart click borders */
          .recharts-wrapper *:focus,
          .recharts-wrapper *:focus-visible,
          .recharts-bar:focus,
          .recharts-pie-sector:focus,
          .recharts-surface:focus {
            outline: none !important;
            border: none !important;
            box-shadow: none !important;
          }
          
          /* 移除所有recharts元素的默认焦点样式 / Remove default focus styles for all recharts elements */
          .recharts-wrapper * {
            outline: none !important;
          }
        `
      }} />
    </div>
  );
};

export default Overview;
